import { createSelector } from 'reselect'
import T from 'i18n-react'
import * as R from 'ramda'
import P from 'helpers/predicates'
import { curry } from 'lodash'
import _ from 'lodash'
import {
  VIEW_ALL,
  FROM_SIZE,
  rowSize,
  propertiesToExclude,
  ProductHierarchyEnum,
  DefaultSearchFields,
  TRUNCATE
} from './constants'
import {
  entitySelector,
  resultsSelector,
} from 'containers/Entity/selectors'

const activeStateSelector = state => state.project.search.active
const inputValueSelector = state => state.project.search.inputValue
const inputFilterValueSelector = state => state.project.search.inputFilterValue
const fieldFilterValueSelector = state => state.project.search.fieldFilterValue
const fromStateSelector = state => state.project.search.from
const sizeStateSelector = state => state.project.search.size
const filtersStateSelector = state => state.project.search.filters
const fieldFiltersStateSelector = state => state.project.search.fieldFilters
const searchFiltersStateSelector = state => state.project.search.searchFilters
const tempQuickFiltersStateSelector = state => state.project.search.tempQuickFilters
const totalStateSelector = state => state.project.search.total
const quickFiltersStateSelector = state => state.project.search.quickFilters
const pinnedDrawerStateSelector = state => state.project.search.pinnedDrawer
const rowSizeStateSelector = state => state.project.search.rowSize
const menuActiveStateSelector = state => state.project.search.menuActive
const columnIndexStateSelector = state => state.project.search.columnIndex
const listOrderStateSelector = state => state.project.search.listOrder
const customColOrderStateSelector = state => state.project.search.customColOrder
const headerFiltersStateSelector = state => state.project.search.headerFilters
const sortByStateSelector = state => state.project.search.sortBy
const sortTypeStateSelector = state => state.project.search.sortType
const sortIndexStateSelector = state => state.project.search.sortIndex
const maxRowSizeStateSelector = state => state.project.search.maxRowSize
const inputHeaderSourceStateSelector = state => state.project.search.inputHeaderSource

const customViewsSelector = state => state.user.customViews
const publicViewStateSelector = state => state.project.search.makePublicView
const viewLabelStateSelector = state => state.project.search.viewLabel
const selectedCustomViewSelector = state => state.project.search.customView

const searchUiStateSelector = state => state.project.search.searchFormElements
const productFiltersStateSelector = state => state.project.search.productHierarchy
const publicCustomViewsStateSelector = state => state.project.search.publicCustomView

const selectedTempCustomViewSelector = state => state.project.search.tempCustomView

const tempProductFiltersStateSelector = state => state.project.search.tempProductFilters
const tempSearchFiltersStateSelector = state => state.project.search.tempSearchFilters
const tempSelectedProductFiltersStateSelector = state => state.project.search.tempSelectedProductFilters
const tempSelectedSearchFiltersStateSelector = state => state.project.search.tempSelectedSearchFilters
const getActivePublicViewStateSelector = state => state.project.search.publicView

const defaultViewSelector = state => state.user.defaultView
const tempDefaultViewSelector = state => state.project.search.defaultView

const collapsedSearchSectionsStateSelector = state => state.project.search.collapsedSearchSections
const selectedProductHierarchies = state => state.project.search.productHierarchyUiState

const projectFiltersEntities = state => entitySelector(state).project_filters
const projectEntities = state => entitySelector(state).projects

const allDefaultViewSelectors = createSelector(
  defaultViewSelector,
  tempDefaultViewSelector,
  (view, temp) => {
    if (temp === '') {
      return view
    } else {
      return temp
    }
  }
)

const allProductHierarchyStateSelector = state => {
  let currentState = state.project.search.productHierarchy
  let arr = []
  Object.values(ProductHierarchyEnum).forEach((val) => {
    if (val.label in currentState) {
      arr.push(currentState[val.label].source)
    }
  })
  return arr
}

const productHierarchyUiStateSelector = state => {
  let currentState = state.project.search.productHierarchyUiState
  let arr = []
  Object.values(ProductHierarchyEnum).forEach((val) => {
    if (val.label in currentState) {
      arr.push(currentState[val.label])
    }
  })
  return arr
}

const productHierarchyValueSelector = curry((state, type) => {
  return state.project.search.productHierarchyUiState[type.label]
})

const getActiveTempCustomViewSelector = createSelector(
  state => state.project.search.tempCustomView,
  state => state.user.customViews,
  (active, customViews) => {
    return customViews.find((view) =>  view.value === active)
  }
)

const getActivePublicViewsSelector = createSelector(
  state => state.project.search.publicView,
  state => state.project.search.publicCustomView,
  state => state.user.customViews,
  (active, publicViews, customViews) => {
    let views = customViews.concat(publicViews)
    return views.filter(view => view.value === active)[0]
  }
)

const getActiveCustomViewsSelector = createSelector(
  state => state.project.search.customView,
  state => state.project.search.publicCustomView,
  state => state.user.customViews,
  (active, publicViews, customViews) => {
    let views = customViews.concat(publicViews)
    return views.filter(view => view.value === active)[0]
  }
)

const depthSelector = createSelector(
  state => state.project.search.productHierarchyUiState,
  (currentState) => {
    let depth = 1
    Object.values(ProductHierarchyEnum).forEach((tier) => {
      if(currentState[tier.label].length === 0) {
        return
      } else {
        depth = tier.value + 2
      }
    })
    return depth
  }
)
// Data table
const formatListFunc = (property, sources) => (
  sources.reverse().reduce((result, {id, ...rest}) => {
    const key = rest[property] ? rest[property] : ''
    const previous = result[key]
    const updated = previous ? [...previous, id] : [id]
    return {
      ...result,
      [key]: updated
    }
  }, {})
)

const productSkusSelector = createSelector(
  productFiltersStateSelector,
  selectedProductHierarchies,
  (filters, productHierarchy) => {
    return productHierarchy['Segment'].concat(filters['Product Sku'].source)
  }
)

const productHierarchyListSelector = createSelector(
  (state) => entitySelector(state).product_hierarchy_views,
  (entities) => {
    return entities
  }
)

const tempSearchFilterFormElementsSelector = createSelector(
  (state) => entitySelector(state).project_filters,
  tempSearchFiltersStateSelector,
  (formElements, ids) => {
    ids = ids.concat(DefaultSearchFields)
    if (formElements) {
      return ids.map((id) => {
        return formElements[id] ? formElements[id].type : ''
      })
    }
  }
)

const tempProductFiltersSelector = createSelector(
  productHierarchyListSelector,
  tempProductFiltersStateSelector,
  tempSelectedProductFiltersStateSelector,
  (entities, filters, selected) => {
    let obj = {}
    Object.values(ProductHierarchyEnum).forEach((val) => {
      obj[val.label] = []
    })

    filters.flat(1).forEach((filter) => {
      let entity = entities[filter]
      entity['checked'] = selected.flat(1).includes(filter)
      entity['value'] = entity['id']
      entity['type'] = entity['name']
      entity['index'] = parseInt(entity['id'])
      entity['category'] = Object.values(ProductHierarchyEnum)[entity['rank'] - 2].label

      obj[entity['category']].push(entity)
    })
    return obj
  }
)

const searchFilterFormElementsSelector = createSelector(
  (state) => entitySelector(state).project_filters,
  searchUiStateSelector,
  (formElements, ids) => {
    ids = ids.concat(DefaultSearchFields)
    if (formElements) {
      let arr = ids.map((id) => {
        return formElements[id] ? formElements[id].type : ''
      })
      return arr
    }
  }
)

const nextFromSelector = createSelector(
  fromStateSelector,
  totalStateSelector,
  (from, total) => {
    let nextFrom = FROM_SIZE + from
    if (nextFrom > total) {
      nextFrom = total
    }
    return nextFrom
  }
)

const listEntitySelector = createSelector(
  state => entitySelector(state).projects,
  state => resultsSelector(state).projects,
  (entities, results) => {
    if (P.isNotNil(entities) && P.isNotNil(results))
      return R.map(R.flip(R.prop)(entities))(results)
    else
      return []
  }
)

// Query Filters
const rowSizeSelector = state => R.compose(
  // R.append({ label: VIEW_ALL, value: VIEW_ALL }),
  R.map((value) => ({ label: value, value }))
)(rowSize)

const rowSizeValueSelector = createSelector(
  maxRowSizeStateSelector,
  rowSizeStateSelector,
  (max, value) => R.ifElse(
    R.equals(VIEW_ALL),
    R.always(max),
    R.always(value)
  )(value)
)

const quickFiltersSelector = createSelector(
  quickFiltersStateSelector,
  R.values
)

const tempQuickFiltersSelector = createSelector(
  tempQuickFiltersStateSelector,
  R.values
)

const filtersEntityStateSelector = state => {
  const filters = entitySelector(state)['project_filters']
  return filters ? filters : {}
}

const allQuickFiltersSelector = createSelector(
  quickFiltersSelector,
  R.compose(
    R.uniq,
    R.reduce((acc, filters) => R.concat(filters, acc), []),
    R.map(R.prop('filters')),
    R.filter(R.propEq('enabled', true)),
    R.values
  )
)

const filtersEntitySelector = createSelector(
  filtersEntityStateSelector,
  filtersStateSelector,
  (filters, selected) => {
    const filterWithChecked = R.map(filter => ({ ...filter, checked: false }))(filters)
    return R.compose(
      R.values,
      R.reduce(
        (acc, id) => R.assocPath([id, 'checked'], true, acc),
        filterWithChecked
      ),
      R.filter(R.has(R.__, filters))
    )(selected)
  }
)

const categoryLens = R.lensProp('category')
const typeLens = R.lensProp('type')

const translatedFiltersEntitySelector = createSelector(
  filtersEntitySelector,
  R.compose(
    R.map(R.over(
      categoryLens,
      category => T.translate(`search.category.${category}`)
    )),
    R.map(R.over(
      typeLens,
      type => T.translate(`search.type.${type}`)
    )),
    R.map(val => R.assoc('value', val.type, val))
  )
)

const filtersSelector = createSelector(
  translatedFiltersEntitySelector,
  (filters) => {
    let sortByCategoryAndType = R.comparator((a, b) => {
      let result = a.category < b.category
      if (a.category === b.category)
        result = a.type < b.type
      return result
    })
    return R.compose(
      R.sort(sortByCategoryAndType),
      R.reject((filter) => propertiesToExclude.includes(filter.value)))(filters)
  }
)

const containsLower = R.curry((a, b) => R.compose(
  R.apply(R.contains),
  R.map(R.toLower),
  (a, b) => [a, b]
)(a,b))

const groupedFiltersSelector = createSelector(
  filtersSelector,
  inputFilterValueSelector,
  (filters, input) => {
   return R.compose(
      R.reduce((acc, value) => {
        let values = R.append(value, acc[value.category])
        return R.assoc(value.category, values, acc)
      }, {}),
      R.filter(R.compose(
        (val) => containsLower(input, val),
        R.prop('type')
      ))
    )(filters)
  }
)

const queryFiltersSelector = createSelector(
  filtersEntitySelector,
  R.compose(
    R.map(R.prop('type')),
    R.filter(R.propEq('checked', true))
  )
)



// Field Filters
const fieldsEntitySelector = createSelector(
  filtersEntityStateSelector,
  searchFiltersStateSelector,
  (filters, selected) => {

    const filterWithChecked = R.map(filter => ({ ...filter, checked: false }))(filters)
    return R.compose(
      R.values,
      R.reduce(
        (acc, id) => R.assocPath([id, 'checked'], true, acc),
        filterWithChecked
      ),
      R.filter(R.has(R.__, filters))
    )(selected)
  }
)

const fieldFiltersEntitySelector = createSelector(
  filtersEntityStateSelector,
  searchFiltersStateSelector,
  (filters, selected) => {
    const filterWithChecked = R.map(filter => ({ ...filter, checked: false }))(filters)
    return R.compose(
      R.values,
      R.reduce(
        (acc, id) => R.assocPath([id, 'checked'], true, acc),
        filterWithChecked
      ),
      R.filter(R.has(R.__, filters))
    )(selected)
  }
)

const translatedFieldFiltersEntitySelector = createSelector(
  fieldFiltersEntitySelector,
  R.compose(
    R.map(R.over(
      categoryLens,
      category => T.translate(`search.category.${category}`)
    )),
    R.map(R.over(
      typeLens,
      type => T.translate(`search.type.${type}`)
    )),
    R.map(val => R.assoc('value', val.type, val))
  )
)

const fieldFiltersSelector = createSelector(
  translatedFieldFiltersEntitySelector,
  (filters) => {
    let sortByCategoryAndType = R.comparator((a, b) => {
      let result = a.category < b.category
      if (a.category === b.category)
        result = a.type < b.type
      return result
    })
    return R.compose(
      R.sort(sortByCategoryAndType),
      R.reject((filter) => propertiesToExclude.includes(filter.value)))(filters)
  }
)

const groupedFieldFiltersSelector = createSelector(
  fieldFiltersSelector,
  fieldFilterValueSelector,
  (filters, input) => {
    return R.compose(
      R.reduce((acc, value) => {
        let values = R.append(value, acc[value.category])
        return R.assoc(value.category, values, acc)
      }, {}),
      R.filter(R.compose(
        (val) => containsLower(input, val),
        R.prop('type')
      )),
      R.filter(R.complement(R.propEq)('value', 'id'))
    )(filters)
  }
)

const searchFieldsSelector = createSelector(
  fieldFiltersSelector,
  searchFilterFormElementsSelector,
  searchFiltersStateSelector,
  (filters, searchFilters, selected) => {
    return (
      R.compose(
        R.reduce((acc, value) => {
          let values = R.append(value, acc[value.category])
          return R.assoc(value.category, values, acc)
        }, {}),
        R.map(
          (val) => R.assoc('checked', selected.includes(val.value), val)
        ),
        R.filter(({ value }) => R.contains(value)(searchFilters))
      )(filters)
    )
  }
)

const noneCheckedSelector = createSelector(
  searchFilterFormElementsSelector,
  searchFiltersStateSelector,
  (searchFilters, selected) => {
    return selected.length === 1
  }
)

const collapsedSelector = createSelector(
  collapsedSearchSectionsStateSelector,
  (collapsed) => {
    return collapsed.length
  }
)

const tempSelector = createSelector(
  fieldFiltersSelector,
  fieldFilterValueSelector,
  tempSearchFilterFormElementsSelector,
  tempSelectedSearchFiltersStateSelector,
  (filters, input, searchFilters, selected) => {
    return (
      R.compose(
        R.reduce((acc, value) => {
          let values = R.append(value, acc[value.category])
          return R.assoc(value.category, values, acc)
        }, {}),
        R.map(
          (val) => R.assoc('checked', selected.includes(val.value), val)
        ),
        R.filter(R.compose(
          (val) => containsLower(input, val),
          R.prop('type')
        )),
        R.filter(({ value }) => R.contains(value)(searchFilters))
      )(filters)
    )
  }
)

// incorrect must not use temp
const searchFiltersSelector = createSelector(
  fieldsEntitySelector,
  (fields) => R.compose(
    R.uniq,
    R.concat(['id']),
    R.map(R.prop('type')),
    R.filter(R.propEq('checked', true))
  )(fields)
)

// filter -> sort -> reduce
const headersSelector = createSelector(
  filtersEntityStateSelector,
  searchFiltersStateSelector,
  listOrderStateSelector,
  (filters, display, listOrder) => {
    return (R.compose(
    R.sort(R.comparator((
      { type: typeA, index: indexA, path: [pathA], count: countA },
      { type: typeB, index: indexB, path: [pathB], count: countB },
    ) => {
      let result = false
      // determines if the same object
      const isArray = countA && countB
      const isSamePath = listOrder[pathA] === listOrder[pathB]
      const isSameType = typeA === typeB
      const isSameIndex = indexA === indexB

      if (isArray && isSamePath) {
        if (isSameIndex) {
          result = listOrder[typeA] < listOrder[typeB]
        } else {
          result = indexA < indexB
        }
      } else {
        result = listOrder[typeA] < listOrder[typeB]
      }
      return result
    })),
    R.reduce((acc, { type, count }) => {
      let headers = []
      if (count) {
        let [start, end] = R.split('.', type)
        headers = R.times(n => ({
          index: n,
          type,
          count,
          path: end? [start, n, end] : [start, n]
        }))(count)
      } else {
        headers = [{ count, type, path: R.split('.', type) }]
      }
      return R.concat(acc, headers)
    })([]),
    R.filter(({ type }) => R.contains(type)(display)),
    R.filter(({ type }) => P.isNotNil(listOrder[type])),
    R.values
  )(filters))
  }
)

const unformattedHeadersSelector = createSelector(
  headersSelector, 
  R.map(obj => obj.type)
)

const formattedHeadersSelector = createSelector(
  headersSelector,
  R.map(({ type, index }) => R.ifElse(
    R.isNil,
    R.always(T.translate(`search.type.${type}`)),
    R.always(T.translate(`search.type.${type}`) + ` ${index + 1}`)
  )(index))
)

const pathsSelector = createSelector(
  headersSelector,
  R.map(R.prop('path'))
)

const listSelector = createSelector(
  listEntitySelector,
  pathsSelector,
  formattedHeadersSelector,
  (lists, paths, headers) => {
    lists = lists.map(ls => {
      TRUNCATE.forEach(({label, value}) => {
        const str = _.get(ls, label)
        const toTruncate = (_.isString(str)) && (str.length > value)
        if (toTruncate) 
          _.set(ls, label, str.substring(0, value).concat('...'))
      })
      return ls
    })
    const table = R.compose(
      R.prepend(headers),
      R.map(row => {
        return R.map(path => (R.path(path, row)))(paths)
      }),
    )(lists)
    return table
  }
)

const currentHeaderFilterValue = createSelector(
  headersSelector,
  columnIndexStateSelector,
  headerFiltersStateSelector,
  (headers, index, filters) => {
    let type = R.path([index, 'type'])(headers)
    return R.compose(
      R.ifElse(
        R.isNil,
        R.always(''),
        R.identity
      ),
      R.prop(type)
    )(filters)
  }
)

const currentSortValue = createSelector(
  headersSelector,
  columnIndexStateSelector,
  sortTypeStateSelector,
  sortByStateSelector,
  (headers, index, sortType, sortBy) => {
    let type = R.path([index, 'type'])(headers)
    return R.ifElse(
      R.equals(sortType),
      R.always(sortBy),
      R.always(''),
    )(type)
  }
)

const currentSortType = createSelector(
  headersSelector,
  columnIndexStateSelector,
  (headers, index) => R.path([index, 'type'])(headers)
)

const sortEnabled = createSelector(
  headersSelector,
  columnIndexStateSelector,
  (headers, index, sortType, sortBy) => {
    let count = R.path([index, 'count'])(headers)
    return R.ifElse(
      R.lt(1),
      R.F,
      R.T,
    )(count)
  }
)

const headerFiltersArraySelector = createSelector(
  headerFiltersStateSelector,
  R.compose(
    R.filter(R.propSatisfies(P.isNotEmptyOrNil, '1')),
    R.toPairs
  )
)

const chipFiltersSelector = createSelector(
  headerFiltersArraySelector,
  R.reduce((acc, [key, values]) => {
    return (R.compose(
      R.concat(acc),
      R.map(R.append(R.__, [key]))
    )(values))
  }
  , [])
)

const queryFieldFiltersSelector = createSelector(
  headerFiltersArraySelector,
  R.map(([key, value]) => `${key}:[${value}]`)
)

const headerFilterValue = createSelector(
  headersSelector,
  headerFiltersStateSelector,
  (headers, filters) => R.map(R.compose(
    R.ifElse(
      P.isNotEmptyOrNil,
      R.always(true),
      R.always(false)
    ),
    R.flip(R.prop)(filters),
    R.prop('type')
  ))(headers)
)

const productSourceSelector = (() => {
  const sourceSelectorHoc = (type) => {
    return createSelector(
      (state) => state.project.search.productHierarchy[type].source,
      (state) => state.entities.product_hierarchy_views,
      (state) => state.project.search.productHierarchyUiState[type],
      (refs, source, selected) => {
        if(!source){
          return []
        }

        let orderedRefs = []
        refs.forEach((ref) => {
          if(selected.includes(ref)){
            return
          } else {
            orderedRefs.push(ref)
          }
        })
        selected.forEach((ref) => {
          orderedRefs.push(ref)
        })

        return (orderedRefs.map((ref) => source[ref]))
      }
    )
  }

  const selectorList = Object.keys(ProductHierarchyEnum)
      .reduce((result, key) => {
          const name = ProductHierarchyEnum[key].label
          return {
          ...result,
          [name]: createSelector(
              sourceSelectorHoc(name),
              (sources) => formatListFunc('name', sources)
            )
          }
      }, {})

  const getSelector = (type) => {
    return selectorList[type.label]
  }
  return curry((state, type) => getSelector(type)(state))
})()

export {
  customViewsSelector,
  listSelector,
  inputValueSelector,
  inputFilterValueSelector,
  fromStateSelector,
  sizeStateSelector,
  filtersStateSelector,
  totalStateSelector,
  nextFromSelector,
  filtersSelector,
  groupedFiltersSelector,
  queryFiltersSelector,
  activeStateSelector,
  fieldFiltersSelector,
  groupedFieldFiltersSelector,
  searchFiltersSelector,
  unformattedHeadersSelector,
  fieldFilterValueSelector,
  searchFiltersStateSelector,
  fieldFiltersStateSelector,
  tempQuickFiltersSelector,
  tempQuickFiltersStateSelector,
  quickFiltersStateSelector,
  quickFiltersSelector,
  allQuickFiltersSelector,
  pinnedDrawerStateSelector,
  rowSizeSelector,
  rowSizeStateSelector,
  rowSizeValueSelector,
  maxRowSizeStateSelector,
  menuActiveStateSelector,
  listOrderStateSelector,
  customColOrderStateSelector,
  columnIndexStateSelector,
  headersSelector,
  currentHeaderFilterValue,
  currentSortValue,
  currentSortType,
  sortEnabled,
  sortIndexStateSelector,
  sortByStateSelector,
  sortTypeStateSelector,
  queryFieldFiltersSelector,
  headerFilterValue,
  headerFiltersArraySelector,
  chipFiltersSelector,
  inputHeaderSourceStateSelector,

  searchFieldsSelector,
  searchUiStateSelector,
  productFiltersStateSelector,
  productHierarchyValueSelector,
  productSourceSelector,
  productHierarchyUiStateSelector,
  depthSelector,
  productSkusSelector,
  searchFilterFormElementsSelector,
  productHierarchyListSelector,
  allProductHierarchyStateSelector,
  getActiveCustomViewsSelector,
  selectedCustomViewSelector,
  viewLabelStateSelector,
  publicCustomViewsStateSelector,
  publicViewStateSelector,
  selectedTempCustomViewSelector,
  getActiveTempCustomViewSelector,

  tempSelector,
  tempProductFiltersSelector,

  tempProductFiltersStateSelector,
  tempSearchFiltersStateSelector,
  tempSelectedProductFiltersStateSelector,
  tempSelectedSearchFiltersStateSelector,
  headerFiltersStateSelector,

  getActivePublicViewStateSelector,
  getActivePublicViewsSelector,
  defaultViewSelector,
  tempDefaultViewSelector,
  allDefaultViewSelectors,
  noneCheckedSelector,
  collapsedSelector,
  selectedProductHierarchies,
  projectFiltersEntities,
  projectEntities
}
